import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Building2, ShoppingBag, FileText, AlertTriangle, Save, Loader2,
  CheckCircle, XCircle, Plus, Trash2, Upload, File, Sparkles, Lock,
  HelpCircle, Coins, Calendar, Image as ImageIcon, Camera, MessageCircle, Link2
} from 'lucide-react';
import { API_URL } from '../../config';
import ExistingCommentsReplier from './ExistingCommentsReplier';

// ============================================================
// BOT STUDIO - Configuration simplifiée de l'agent IA
// Utilisé par l'ADMIN (Bot Studio) et le CLIENT (Page Facebook)
// selon l'autorisation self_config_allowed.
// ============================================================

const authHeaders = (json = true) => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  ...(json ? { 'Content-Type': 'application/json' } : {})
});

const TONES = [
  { id: 'amical',        label: '😊 Amical',        desc: 'Chaleureux et proche' },
  { id: 'professionnel', label: '💼 Professionnel', desc: 'Poli et précis' },
  { id: 'energique',     label: '⚡ Énergique',     desc: 'Enthousiaste' },
  { id: 'concis',        label: '✂️ Concis',        desc: 'Réponses courtes et naturelles, comme un humain' },
];
const LANGUAGES = [
  { id: 'auto',  label: '🌍 Automatique (langue du client)' },
  { id: 'mg',    label: '🇲🇬 Malagasy uniquement' },
  { id: 'fr',    label: '🇫🇷 Français uniquement' },
  { id: 'mg_fr', label: '🇲🇬🇫🇷 Malagasy + Français' },
];
const OBJECTIVES = [
  { id: 'vendeur', label: '💰 Vendeur',   desc: 'Vend et conclut les commandes dans Messenger' },
  { id: 'support', label: '🛟 Support',   desc: 'Aide les clients existants' },
  { id: 'accueil', label: '👋 Accueil',   desc: 'Informe et oriente les visiteurs' },
];

const EMPTY_PRODUCT = { name: '', price: '', currency: 'Ar', description: '', stock: 'available' };

export default function BotConfigPanel({ clientId, hideActivationToggle = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [plan, setPlan] = useState('legacy');     // 'bot' | 'cm' | 'legacy' (formule de la Page)
  const [pageName, setPageName] = useState('');
  const [message, setMessage] = useState(null);
  const [cfg, setCfg] = useState({
    bot_name: '', tone: 'amical', languages: 'auto', objective: 'vendeur',
    business_description: '', extra_instructions: '',
    transfer_keywords: '', transfer_message: '', products: [],
    faq: [], is_active: false,
    sale_type: 'payment_confirmed', payment_keywords: '', payment_intent_keywords: '',
    rdv_enabled: false, rdv_keywords: '',
    comment_dm_enabled: false, comment_reply_enabled: false, comment_reply_mode: 'fixed',
    comment_reveal_price_publicly: false,
    comment_reply_fixed_text: '', comment_monitor_mode: 'all',
    comment_dm_instructions: '', comment_reply_instructions: '',
  });
  // Publications suivies (mode "selected")
  const [monitoredPosts, setMonitoredPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [newPostUrl, setNewPostUrl] = useState('');
  const [newPostLabel, setNewPostLabel] = useState('');
  const [addingPost, setAddingPost] = useState(false);
  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('catalogue');
  // Photos du bot
  const [medias, setMedias] = useState([]);
  const [mediaTitle, setMediaTitle] = useState('');
  const [mediaDesc, setMediaDesc] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, docsRes, mediaRes] = await Promise.all([
        fetch(`${API_URL}/api/bot/config/${clientId}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/bot/knowledge/${clientId}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/bot/media/${clientId}`, { headers: authHeaders() }),
      ]);
      const cfgData = await cfgRes.json();
      const docsData = await docsRes.json();
      const mediaData = await mediaRes.json();
      if (cfgData.success) {
        setCfg(cfgData.config);
        setCanEdit(cfgData.can_edit);
        setPlan(cfgData.plan || 'legacy');
        setPageName(cfgData.page_name);
      }
      if (docsData.success) setDocuments(docsData.documents);
      if (mediaData.success) setMedias(mediaData.medias);
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const loadMonitoredPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMonitoredPosts(data.posts);
    } catch (e) { console.error(e); }
    finally { setPostsLoading(false); }
  }, [clientId]);

  useEffect(() => {
    if (cfg.comment_monitor_mode === 'selected') loadMonitoredPosts();
  }, [cfg.comment_monitor_mode, loadMonitoredPosts]);

  const addMonitoredPost = async () => {
    if (!newPostUrl.trim()) return;
    setAddingPost(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ post_url: newPostUrl, label: newPostLabel })
      });
      const data = await res.json();
      if (data.success) {
        setNewPostUrl('');
        setNewPostLabel('');
        loadMonitoredPosts();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } finally { setAddingPost(false); }
  };

  // Publications DEJA existantes de la Page (pour les choisir sans attendre un premier commentaire)
  const [pagePosts, setPagePosts] = useState([]);
  const [pagePostsLoading, setPagePostsLoading] = useState(false);
  const [pagePostsError, setPagePostsError] = useState('');
  const [togglingPostId, setTogglingPostId] = useState(null);

  const loadPagePosts = useCallback(async () => {
    setPagePostsLoading(true); setPagePostsError('');
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}/page-posts`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setPagePosts(data.posts);
      else setPagePostsError(data.message || 'Impossible de charger les publications.');
    } catch (e) { setPagePostsError('Erreur réseau.'); }
    finally { setPagePostsLoading(false); }
  }, [clientId]);

  useEffect(() => {
    if (cfg.comment_monitor_mode === 'selected') loadPagePosts();
  }, [cfg.comment_monitor_mode, loadPagePosts]);

  const togglePagePost = async (post) => {
    setTogglingPostId(post.id);
    try {
      const existing = monitoredPosts.find(m => m.post_id === post.id);
      if (existing) {
        await toggleMonitoredPost(existing.id);
      } else {
        const res = await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ post_id: post.id, label: (post.message || 'Publication').slice(0, 60) }),
        });
        const data = await res.json();
        if (data.success) await loadMonitoredPosts();
        else setMessage({ type: 'error', text: data.message });
      }
    } finally { setTogglingPostId(null); }
  };

  // Reponse PERSONNALISEE par publication (texte fixe ou IA + infos propres a la publication).
  // Affichee directement sous chaque publication activee (pas de clic supplementaire).
  const [postDrafts, setPostDrafts] = useState({});   // { [idDocPublication]: { reply_mode, reply_text, reply_info } }
  const [savingPostId, setSavingPostId] = useState(null);

  const draftOf = (entry) => postDrafts[entry.id] || {
    reply_mode: entry.reply_mode || 'default', reply_text: entry.reply_text || '', reply_info: entry.reply_info || '',
  };
  const setDraft = (entry, patch) => setPostDrafts(prev => ({ ...prev, [entry.id]: { ...draftOf(entry), ...patch } }));

  const savePostReply = async (entry) => {
    const draft = draftOf(entry);
    setSavingPostId(entry.id);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts/${entry.id}/reply-config`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (data.success) {
        setMonitoredPosts(prev => prev.map(p => p.id === entry.id ? { ...p, ...draft } : p));
        setPostDrafts(prev => { const n = { ...prev }; delete n[entry.id]; return n; });
        setMessage({ type: 'ok', text: 'Réponse de la publication enregistrée.' });
      } else setMessage({ type: 'error', text: data.message });
    } finally { setSavingPostId(null); }
  };

  const renderPostReplyEditor = (entry) => {
    const draft = draftOf(entry);
    const dirty = !!postDrafts[entry.id];
    return (
      <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 w-full">
        <div className="text-gray-300 text-[11px] font-medium">Réponse aux commentaires de cette publication</div>
        <div className="flex flex-wrap gap-1.5">
          {[['default', 'Réglage général'], ['fixed', '✂️ Message fixe'], ['ai', '🤖 Généré par l\'IA']].map(([m, label]) => (
            <button key={m} onClick={() => setDraft(entry, { reply_mode: m })}
              className={`px-3 py-1.5 rounded-lg text-xs border ${draft.reply_mode === m ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}>
              {label}
            </button>
          ))}
        </div>
        {draft.reply_mode === 'fixed' && (
          <textarea value={draft.reply_text} maxLength={500} rows={2}
            onChange={e => setDraft(entry, { reply_text: e.target.value })}
            placeholder="Ex : Merci ! La partie 3 sort le 20 octobre, abonnez-vous à la page 🙂"
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500" />
        )}
        {draft.reply_mode === 'ai' && (
          <textarea value={draft.reply_info} maxLength={2000} rows={3}
            onChange={e => setDraft(entry, { reply_info: e.target.value })}
            placeholder="Infos sur CETTE publication pour l'IA (date de sortie, prix, lien, consignes…) — l'IA s'en sert pour répondre aux commentaires de cette publication."
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500" />
        )}
        {draft.reply_mode !== 'default' && (
          <p className="text-gray-500 text-[10px]">Sur cette publication, la réponse publique est envoyée même si l'interrupteur « Répondre publiquement » du haut est éteint.</p>
        )}
        <button onClick={() => savePostReply(entry)} disabled={savingPostId === entry.id || !dirty}
          className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-40">
          {savingPostId === entry.id ? 'Enregistrement…' : dirty ? 'Enregistrer' : '✓ Enregistré'}
        </button>
      </div>
    );
  };

  // Publications suivies absentes de la liste ci-dessus (ajoutees par lien, ou anciennes) : evite les doublons
  const extraMonitored = monitoredPosts.filter(m => !pagePosts.some(pp => pp.id === m.post_id));

  const deleteMonitoredPost = async (postDocId) => {
    await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts/${postDocId}`, {
      method: 'DELETE', headers: authHeaders()
    });
    loadMonitoredPosts();
  };

  const toggleMonitoredPost = async (postDocId) => {
    const res = await fetch(`${API_URL}/api/bot/config/${clientId}/monitored-posts/${postDocId}/toggle`, {
      method: 'POST', headers: authHeaders()
    });
    const data = await res.json();
    if (data.success) {
      setMonitoredPosts(prev => prev.map(p => p.id === postDocId ? { ...p, enabled: data.enabled } : p));
    }
  };

  // Publication manuelle test (base du futur module CM, sert aussi de demo App Review pages_manage_posts)
  const [publishText, setPublishText] = useState('');
  const [publishImageUrl, setPublishImageUrl] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const publishPost = async () => {
    if (!publishText.trim() && !publishImageUrl.trim()) return;
    setPublishing(true);
    setPublishResult(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}/publish-post`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ message: publishText, image_url: publishImageUrl })
      });
      const data = await res.json();
      setPublishResult({ ok: data.success, text: data.success ? 'Publié sur la Page !' : data.message });
      if (data.success) { setPublishText(''); setPublishImageUrl(''); }
    } catch {
      setPublishResult({ ok: false, text: 'Erreur serveur' });
    } finally { setPublishing(false); }
  };

  // Recharge UNIQUEMENT la liste des documents (ne touche pas au formulaire en cours de saisie)
  const loadDocumentsOnly = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/knowledge/${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setDocuments(data.documents);
    } catch (e) { console.error(e); }
  }, [clientId]);

  const set = (key, value) => setCfg(prev => ({ ...prev, [key]: value }));

  // Active/pause immédiatement (endpoint dédié, pas besoin de cliquer "Enregistrer")
  const toggleActive = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/messenger-bot/toggle`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setCfg(prev => ({ ...prev, is_active: data.is_active }));
      }
    } catch (e) { console.error(e); }
    finally { setToggling(false); }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${clientId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify((({ is_active, ...rest }) => rest)(cfg))   // jamais is_active : un panneau ouvert avant l'activation l'aurait remis sur « pause »
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setSaving(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const res = await fetch(`${API_URL}/api/bot/knowledge/${clientId}/upload`, {
        method: 'POST', headers: authHeaders(false), body: fd
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      if (data.success) loadDocumentsOnly();  // ne recharge PAS le formulaire
    } finally { setUploading(false); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Retirer ce document du cerveau de l\'agent ?')) return;
    await fetch(`${API_URL}/api/bot/knowledge/${clientId}/${docId}`, { method: 'DELETE', headers: authHeaders() });
    loadDocumentsOnly();  // ne recharge PAS le formulaire
  };

  // ── Photos du bot ──
  const loadMediasOnly = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/media/${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMedias(data.medias);
    } catch (e) { console.error(e); }
  }, [clientId]);

  const uploadMedia = async (file) => {
    if (!file) return;
    if (!mediaTitle.trim()) {
      setMessage({ type: 'error', text: 'Donnez d\'abord un nom à la photo (ex : Catalogue robes)' });
      return;
    }
    setMediaUploading(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', mediaTitle);
      fd.append('description', mediaDesc);
      const res = await fetch(`${API_URL}/api/bot/media/${clientId}/upload`, {
        method: 'POST', headers: authHeaders(false), body: fd
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      if (data.success) {
        setMediaTitle('');
        setMediaDesc('');
        loadMediasOnly();
      }
    } finally { setMediaUploading(false); }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    await fetch(`${API_URL}/api/bot/media/${clientId}/${mediaId}`, { method: 'DELETE', headers: authHeaders() });
    loadMediasOnly();
  };

  if (loading) {
    return <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto" /></div>;
  }

  if (!canEdit) {
    return (
      <div className="p-8 text-center bg-white/5 border border-white/10 rounded-2xl">
        <Lock className="w-10 h-10 text-gray-500 mx-auto mb-3" />
        <p className="text-white font-medium">Configuration gérée par l'équipe TDI DIGITAL</p>
        <p className="text-gray-400 text-sm mt-1">
          Votre agent IA est configuré et optimisé pour vous par nos experts.<br />
          Contactez-nous pour toute modification : contact@tdidigital.io
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-3 rounded-xl flex items-center gap-2 text-sm sticky top-2 z-10 ${
          message.type === 'ok'
            ? 'bg-green-500/15 border border-green-500/30 text-green-300'
            : 'bg-red-500/15 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* ── ACTIVATION ── */}
      <div className={`flex items-center justify-between gap-4 p-4 rounded-2xl border ${
        cfg.is_active ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${cfg.is_active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
          <div>
            <div className="text-white font-semibold text-sm">
              {cfg.is_active ? 'Agent IA actif' : 'Agent IA en pause'}
            </div>
            <div className="text-gray-400 text-xs">
              {cfg.is_active ? 'Il répond automatiquement aux messages de la page' : 'Il ne répond à aucun message'}
            </div>
          </div>
        </div>
        {!hideActivationToggle && (
          <button
            onClick={toggleActive}
            disabled={toggling}
            className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 disabled:opacity-50 ${cfg.is_active ? 'bg-green-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.is_active ? 'left-6' : 'left-0.5'}`} />
          </button>
        )}
      </div>

      {/* ── 1. IDENTITÉ DE L'AGENT ── */}
      <Section icon={Bot} title="Votre agent" subtitle="Qui est-il et comment parle-t-il ?">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nom de l'agent</Label>
            <input
              value={cfg.bot_name}
              onChange={(e) => set('bot_name', e.target.value)}
              placeholder="Ex : Steve, Naly, Miora..."
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
            />
          </div>
          <div>
            <Label>Langue des réponses</Label>
            <select
              value={cfg.languages}
              onChange={(e) => set('languages', e.target.value)}
              className="w-full bg-slate-900 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm"
            >
              {LANGUAGES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
        </div>

        <Label className="mt-4">Ton de l'agent</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {TONES.map(t => (
            <ChoiceCard key={t.id} active={cfg.tone === t.id} onClick={() => set('tone', t.id)}
              label={t.label} desc={t.desc} />
          ))}
        </div>

        <Label className="mt-4">Mission de l'agent</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {OBJECTIVES.map(o => (
            <ChoiceCard key={o.id} active={cfg.objective === o.id} onClick={() => set('objective', o.id)}
              label={o.label} desc={o.desc} />
          ))}
        </div>
      </Section>

      {/* ── 2. ENTREPRISE ── */}
      <Section icon={Building2} title="Votre entreprise" subtitle="Ce que l'agent doit savoir sur vous">
        <Label>Décrivez votre activité (l'agent s'en servira pour répondre)</Label>
        <textarea
          value={cfg.business_description}
          onChange={(e) => set('business_description', e.target.value)}
          rows={12}
          maxLength={8000}
          placeholder={`Ex : ${pageName || 'Mon entreprise'} est une agence de voyage à Madagascar. Nous organisons des circuits et réservons des hôtels partout dans l'île. Horaires : lun-sam 8h-18h. Paiement : MVola, Orange Money, espèces.`}
          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 resize-y min-h-[200px]"
        />
        <div className={`text-right text-xs mt-1 ${(cfg.business_description || '').length >= 7600 ? 'text-yellow-300' : 'text-gray-500'}`}>
          {(cfg.business_description || '').length} / 8000 caractères
        </div>
      </Section>

      {/* ── 3. PRODUITS ── */}
      <Section icon={ShoppingBag} title="Vos produits & services" subtitle="Avec les prix exacts — l'agent ne connaîtra que ceux-là">
        <div className="space-y-3">
          {cfg.products.map((p, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                <input
                  value={p.name}
                  onChange={(e) => updateProduct(setCfg, i, 'name', e.target.value)}
                  placeholder="Nom du produit / service"
                  className="flex-1 min-w-[160px] bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                />
                <input
                  value={p.price}
                  onChange={(e) => updateProduct(setCfg, i, 'price', e.target.value)}
                  placeholder="Prix"
                  inputMode="numeric"
                  className="w-24 md:w-32 flex-shrink-0 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                />
                <select
                  value={p.currency}
                  onChange={(e) => updateProduct(setCfg, i, 'currency', e.target.value)}
                  className="w-20 bg-slate-900 border border-white/20 rounded-lg px-2 py-2 text-white text-sm"
                >
                  <option value="Ar">Ar</option><option value="€">€</option><option value="$">$</option>
                </select>
                <button onClick={() => setCfg(prev => ({ ...prev, products: prev.products.filter((_, j) => j !== i) }))}
                  className="p-2 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                value={p.description}
                onChange={(e) => updateProduct(setCfg, i, 'description', e.target.value)}
                placeholder="Description courte (optionnel)"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
              />
            </div>
          ))}
          <button
            onClick={() => setCfg(prev => ({ ...prev, products: [...prev.products, { ...EMPTY_PRODUCT }] }))}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-dashed border-white/20 text-gray-300 hover:bg-white/10 text-sm w-full justify-center"
          >
            <Plus className="w-4 h-4" /> Ajouter un produit / service
          </button>
        </div>
      </Section>

      {/* ── 4. DOCUMENTS (OCR) ── */}
      <Section icon={FileText} title="Documents & catalogues" subtitle="PDF, Word, ou simple photo — extraction automatique par IA">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setDocType('catalogue')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${docType === 'catalogue' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300'}`}>
            🛍️ Catalogue produits
          </button>
          <button onClick={() => setDocType('document')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${docType === 'document' ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300'}`}>
            📄 Document entreprise
          </button>
        </div>

        <label className={`block border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
          uploading ? 'border-blue-500/50 bg-blue-500/5' : 'border-white/20 hover:border-blue-500/50 hover:bg-white/5'
        }`}>
          <input type="file" accept=".pdf,.docx,.txt,.csv,.md,.jpg,.jpeg,.png,.webp" className="hidden"
            disabled={uploading}
            onChange={(e) => { uploadDoc(e.target.files[0]); e.target.value = ''; }} />
          {uploading ? (
            <span className="flex items-center justify-center gap-2 text-blue-300 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Lecture et analyse par l'IA... (~30s)
            </span>
          ) : (
            <span className="flex flex-col items-center gap-1">
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="text-white text-sm">Uploader un fichier ou une photo</span>
              <span className="text-gray-500 text-xs">PDF, DOCX, image (menu, flyer, liste de prix...) — OCR automatique</span>
            </span>
          )}
        </label>

        {documents.length > 0 && (
          <div className="mt-3 space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                <File className={`w-4 h-4 flex-shrink-0 ${doc.doc_type === 'catalogue' ? 'text-purple-300' : 'text-blue-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{doc.title}</div>
                  <div className="text-gray-500 text-xs truncate">{doc.preview}...</div>
                </div>
                <button onClick={() => deleteDoc(doc.id)} className="p-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── PHOTOS DU BOT ── */}
      <Section icon={Camera} title="Photos envoyées par l'agent" subtitle="L'agent envoie la bonne photo dans Messenger quand un client demande à voir (catalogue, produit, menu...)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Nom de la photo (l'agent s'en sert pour choisir)</Label>
            <input
              value={mediaTitle}
              onChange={(e) => setMediaTitle(e.target.value)}
              placeholder="Ex : Catalogue circuits, Menu du jour, Photo produit X"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-500"
            />
          </div>
          <div>
            <Label>Quand l'envoyer ?</Label>
            <input
              value={mediaDesc}
              onChange={(e) => setMediaDesc(e.target.value)}
              placeholder="Ex : quand le client demande à voir les circuits disponibles"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-500"
            />
          </div>
        </div>

        <label className={`block border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
          mediaUploading ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/20 hover:border-purple-500/50 hover:bg-white/5'
        }`}>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={mediaUploading}
            onChange={(e) => { uploadMedia(e.target.files[0]); e.target.value = ''; }} />
          {mediaUploading ? (
            <span className="flex items-center justify-center gap-2 text-purple-300 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Upload en cours...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2 text-white text-sm">
              <ImageIcon className="w-5 h-5 text-gray-400" /> Choisir l'image (JPG/PNG — max 5 MB, 15 photos max)
            </span>
          )}
        </label>

        {medias.length > 0 && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {medias.map(m => (
              <div key={m.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <img src={m.url} alt={m.title} className="w-full h-24 object-cover" />
                <div className="p-2">
                  <div className="text-white text-xs font-medium truncate">{m.title}</div>
                  <div className="text-gray-500 text-xs truncate">{m.description || '—'}</div>
                  <button onClick={() => deleteMedia(m.id)}
                    className="mt-1.5 w-full flex items-center justify-center gap-1 py-1 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs">
                    <Trash2 className="w-3 h-3" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── FAQ ── */}
      <Section icon={HelpCircle} title="Questions fréquentes (FAQ)" subtitle="Réponses exactes prioritaires — l'agent les utilise mot pour mot">
        <div className="space-y-3">
          {cfg.faq.map((f, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
              <div className="flex gap-2">
                <input
                  value={f.question}
                  onChange={(e) => setCfg(prev => ({ ...prev, faq: prev.faq.map((x, j) => j === i ? { ...x, question: e.target.value } : x) }))}
                  placeholder="Question du client (ex : Aiza ianareo no misy ?)"
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                />
                <button onClick={() => setCfg(prev => ({ ...prev, faq: prev.faq.filter((_, j) => j !== i) }))}
                  className="p-2 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={f.answer}
                onChange={(e) => setCfg(prev => ({ ...prev, faq: prev.faq.map((x, j) => j === i ? { ...x, answer: e.target.value } : x) }))}
                placeholder="Réponse exacte de l'agent"
                rows={2}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
              />
            </div>
          ))}
          <button
            onClick={() => setCfg(prev => ({ ...prev, faq: [...prev.faq, { question: '', answer: '' }] }))}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-dashed border-white/20 text-gray-300 hover:bg-white/10 text-sm w-full justify-center"
          >
            <Plus className="w-4 h-4" /> Ajouter une question / réponse
          </button>
        </div>
      </Section>

      {/* ── DÉTECTION DE VENTE (mode vendeur) ── */}
      {cfg.objective === 'vendeur' && (
        <Section icon={Coins} title="Détection des ventes" subtitle="Quand compter une vente et vous alerter sur Telegram ?">
          <div className="space-y-2">
            {[
              { id: 'payment_confirmed', label: '💳 Paiement confirmé', desc: 'Le client dit avoir payé (« efa nandefa vola aho »)' },
              { id: 'payment_intent',    label: '🤝 Paiement ou intention', desc: 'Il a payé OU annonce qu\'il paie maintenant' },
              { id: 'order_with_details', label: '📋 Commande complète', desc: 'Produit + coordonnées suffisent, sans parler de paiement' },
            ].map(o => (
              <ChoiceCard key={o.id} active={cfg.sale_type === o.id} onClick={() => set('sale_type', o.id)}
                label={o.label} desc={o.desc} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Mots de paiement confirmé</Label>
              <input value={cfg.payment_keywords} onChange={(e) => set('payment_keywords', e.target.value)}
                placeholder="j'ai payé, efa nandefa vola, paid"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-xs placeholder-gray-500" />
            </div>
            {cfg.sale_type === 'payment_intent' && (
              <div>
                <Label>Mots d'intention de paiement</Label>
                <input value={cfg.payment_intent_keywords} onChange={(e) => set('payment_intent_keywords', e.target.value)}
                  placeholder="je paie maintenant, handefa vola izao"
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-xs placeholder-gray-500" />
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── RENDEZ-VOUS (optionnel) ── */}
      <Section icon={Calendar} title="Prise de rendez-vous" subtitle="Optionnel — l'agent peut planifier des RDV si votre activité en a besoin">
        <div className="flex items-center justify-between gap-4">
          <div className="text-gray-300 text-sm">
            {cfg.rdv_enabled
              ? 'Activé : l\'agent collecte nom, téléphone, date et heure, puis vous alerte sur Telegram'
              : 'Désactivé : l\'agent ne propose jamais de rendez-vous (recommandé pour un vendeur)'}
          </div>
          <button
            onClick={() => set('rdv_enabled', !cfg.rdv_enabled)}
            className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${cfg.rdv_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.rdv_enabled ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>
        {cfg.rdv_enabled && (
          <div className="mt-3">
            <Label>Mots-clés qui déclenchent la prise de RDV</Label>
            <input value={cfg.rdv_keywords} onChange={(e) => set('rdv_keywords', e.target.value)}
              placeholder="rendez-vous, rdv, fotoana, fihaonana"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-500" />
          </div>
        )}
      </Section>

      {/* ── COMMENTAIRES (Messenger) : offre « Messenger Bot + CM Studio » uniquement ── */}
      {plan === 'bot' && (
        <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-400/30 text-amber-100 text-sm">
          ✨ La gestion des commentaires et Community Manager Studio font partie de l'offre <b>« Messenger Bot + Community Manager Studio »</b>. Écrivez-nous dans Support pour y passer.
        </div>
      )}
      {plan !== 'bot' && (<>
      <Section icon={MessageCircle} title="Réponse aux commentaires" subtitle="Quand quelqu'un commente une publication de votre Page">
        {plan === 'legacy' && (<>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="text-white font-medium text-sm">Envoyer un message privé automatique</div>
            <div className="text-gray-400 text-xs mt-0.5">L'IA lit le commentaire et répond en message privé (Messenger) à la personne.</div>
          </div>
          <button
            onClick={() => set('comment_dm_enabled', !cfg.comment_dm_enabled)}
            className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${cfg.comment_dm_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.comment_dm_enabled ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>
        {cfg.comment_dm_enabled && (
          <input
            value={cfg.comment_dm_instructions}
            onChange={(e) => set('comment_dm_instructions', e.target.value)}
            placeholder="Infos pour l'IA (optionnel) — ex : mentionner la promo en cours"
            maxLength={75}
            className="w-full mb-4 bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white text-xs placeholder-gray-500"
          />
        )}
        </>)}

        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <div className="text-white font-medium text-sm">Répondre aussi publiquement au commentaire</div>
            <div className="text-gray-400 text-xs mt-0.5">Une réponse visible sous le commentaire, en plus (ou à la place) du message privé.</div>
          </div>
          <button
            onClick={() => set('comment_reply_enabled', !cfg.comment_reply_enabled)}
            className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${cfg.comment_reply_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.comment_reply_enabled ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>

        {cfg.comment_reply_enabled && (
          <div className="mb-4 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <ChoiceCard active={cfg.comment_reply_mode === 'fixed'} onClick={() => set('comment_reply_mode', 'fixed')}
                label="✂️ Message fixe" desc="Toujours le même texte, ex : « On vous a envoyé un DM »" />
              <ChoiceCard active={cfg.comment_reply_mode === 'ai'} onClick={() => set('comment_reply_mode', 'ai')}
                label="🤖 Généré par l'IA" desc="L'IA rédige une réponse adaptée à chaque commentaire" />
            </div>
            {cfg.comment_reply_mode === 'fixed' && (
              <input
                value={cfg.comment_reply_fixed_text}
                onChange={(e) => set('comment_reply_fixed_text', e.target.value)}
                placeholder="Ex : Nous vous avons envoyé un message privé, merci ! 🙂"
                maxLength={255}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
              />
            )}
            {cfg.comment_reply_mode === 'ai' && (
              <>
                <input
                  value={cfg.comment_reply_instructions}
                  onChange={(e) => set('comment_reply_instructions', e.target.value)}
                  placeholder="Infos pour l'IA (optionnel) — sinon elle mentionne juste le DM si envoyé"
                  maxLength={75}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 mb-2"
                />
                <div className="flex items-center justify-between gap-3 bg-white/5 rounded-xl px-4 py-2.5">
                  <div>
                    <div className="text-white text-sm">Donner le prix directement dans la réponse publique</div>
                    <div className="text-gray-500 text-xs">Sinon, l'IA invite la personne à consulter son message privé pour le prix</div>
                  </div>
                  <button
                    onClick={() => set('comment_reveal_price_publicly', !cfg.comment_reveal_price_publicly)}
                    className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${cfg.comment_reveal_price_publicly ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.comment_reveal_price_publicly ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <Label className="mt-2">Publications à surveiller</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <ChoiceCard active={cfg.comment_monitor_mode === 'all'} onClick={() => set('comment_monitor_mode', 'all')}
            label="🌐 Toutes les publications" desc="Chaque commentaire sur n'importe quel post déclenche la réponse" />
          <ChoiceCard active={cfg.comment_monitor_mode === 'selected'} onClick={() => set('comment_monitor_mode', 'selected')}
            label="🎯 Publications sélectionnées" desc="Uniquement les posts que vous choisissez ci-dessous" />
        </div>

        {cfg.comment_monitor_mode === 'selected' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white text-xs font-medium">Vos publications Facebook</div>
                <button onClick={loadPagePosts} disabled={pagePostsLoading} className="text-blue-300 hover:text-blue-200 text-[11px] disabled:opacity-50">
                  {pagePostsLoading ? 'Chargement…' : 'Actualiser'}
                </button>
              </div>
              {pagePostsError && <p className="text-red-300 text-xs mb-2">{pagePostsError}</p>}
              {pagePostsLoading && pagePosts.length === 0 ? (
                <div className="py-3 text-center"><Loader2 className="w-5 h-5 text-blue-400 animate-spin mx-auto" /></div>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {pagePosts.map(p => {
                    const entry = monitoredPosts.find(m => m.post_id === p.id);
                    const on = !!(entry && entry.enabled);
                    return (
                      <div key={p.id} className="flex items-center gap-2 bg-black/20 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-xs truncate">{p.message || '(publication sans texte)'}</div>
                          <div className="text-gray-500 text-[10px]">
                            {new Date(p.created_time).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} · {p.comments_count} commentaire{p.comments_count > 1 ? 's' : ''}
                          </div>
                          {on && entry && renderPostReplyEditor(entry)}
                        </div>
                        <button
                          onClick={() => togglePagePost(p)}
                          disabled={togglingPostId === p.id}
                          className={`relative w-9 h-5 rounded-full transition-all flex-shrink-0 disabled:opacity-50 ${on ? 'bg-green-500' : 'bg-gray-600'}`}
                          title={on ? 'Ne plus répondre sur cette publication' : 'Répondre automatiquement sur cette publication'}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-4' : 'left-0.5'}`} />
                        </button>
                      </div>
                    );
                  })}
                  {!pagePostsLoading && !pagePostsError && pagePosts.length === 0 && (
                    <p className="text-gray-500 text-xs text-center py-2">Aucune publication trouvée sur la Page.</p>
                  )}
                </div>
              )}
              <p className="text-gray-500 text-[10px] mt-2">Activez les publications où TeckiA doit répondre automatiquement aux <strong>nouveaux</strong> commentaires. Pour les commentaires déjà reçus, utilisez la section « Répondre aux commentaires existants » plus bas.</p>
            </div>
            <div className="text-gray-400 text-[11px] mb-1.5">Ou ajoutez une publication par son lien :</div>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                value={newPostUrl}
                onChange={(e) => setNewPostUrl(e.target.value)}
                placeholder="Collez le lien du post Facebook (ex : facebook.com/.../posts/123...)"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
              />
              <input
                value={newPostLabel}
                onChange={(e) => setNewPostLabel(e.target.value)}
                placeholder="Nom (optionnel)"
                className="sm:w-40 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
              />
              <button
                onClick={addMonitoredPost}
                disabled={addingPost || !newPostUrl.trim()}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50 flex-shrink-0"
              >
                {addingPost ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Ajouter
              </button>
            </div>

            {postsLoading ? (
              <div className="py-3 text-center"><Loader2 className="w-5 h-5 text-blue-400 animate-spin mx-auto" /></div>
            ) : monitoredPosts.length === 0 ? (
              <p className="text-gray-500 text-xs text-center py-2">Aucune publication sélectionnée — aucun commentaire ne sera traité tant que la liste est vide.</p>
            ) : extraMonitored.length === 0 ? null : (
              <div className="space-y-1.5">
                {extraMonitored.map(p => (
                  <div key={p.id} className="flex items-center gap-2 bg-black/20 rounded-lg px-3 py-2">
                    <Link2 className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-xs truncate flex items-center gap-1.5">
                        {p.label || 'Publication'}
                        {p.auto_detected && !p.enabled && (
                          <span className="text-yellow-300 bg-yellow-500/10 px-1.5 py-0.5 rounded text-[10px] flex-shrink-0">nouveau, à activer</span>
                        )}
                      </div>
                      <div className="text-gray-500 text-[10px] truncate">{p.post_id}</div>
                      {p.enabled && renderPostReplyEditor(p)}
                    </div>
                    <button
                      onClick={() => toggleMonitoredPost(p.id)}
                      className={`relative w-9 h-5 rounded-full transition-all flex-shrink-0 ${p.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                      title={p.enabled ? 'Désactiver' : 'Activer'}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${p.enabled ? 'left-4' : 'left-0.5'}`} />
                    </button>
                    <button onClick={() => deleteMonitoredPost(p.id)} className="p-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!postsLoading && (
              <p className="text-gray-500 text-[10px] mt-2">
                💡 Les publications commentées sont détectées automatiquement (elles apparaissent "à activer" ci-dessus) — le lien collé ci-dessus est en meilleur effort et peut ne pas fonctionner avec les liens de partage récents de Facebook.
              </p>
            )}
          </div>
        )}
      </Section>

      <Section icon={MessageCircle} title="Répondre aux commentaires existants" subtitle="Réponse automatique ou par l'IA aux commentaires déjà reçus sur une publication">
        <ExistingCommentsReplier clientId={clientId} canEdit={canEdit} defaultFixedText={cfg.comment_reply_fixed_text} postConfigs={monitoredPosts} />
      </Section>

      {canEdit && (
        <Section icon={Sparkles} title="🧪 Publication test (bêta)" subtitle="Publie un post directement sur votre Page — sert à tester le futur module Community Manager IA">
          <Label>Texte du post</Label>
          <textarea
            value={publishText}
            onChange={e => setPublishText(e.target.value)}
            rows={3}
            placeholder="Ex: Découvrez notre nouvelle offre ce mois-ci !"
            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
          />
          <div className="mt-2">
            <Label>URL image (optionnel)</Label>
            <input
              type="text"
              value={publishImageUrl}
              onChange={e => setPublishImageUrl(e.target.value)}
              placeholder="https://..."
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={publishPost}
              disabled={publishing || (!publishText.trim() && !publishImageUrl.trim())}
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50"
            >
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Publier sur la Page
            </button>
            {publishResult && (
              <span className={`text-xs ${publishResult.ok ? 'text-green-400' : 'text-red-400'}`}>{publishResult.text}</span>
            )}
          </div>
        </Section>
      )}

      </>)}

      {/* ── 5. TRANSFERT HUMAIN ── */}
      <Section icon={AlertTriangle} title="Passage à un humain" subtitle="Quand l'agent doit vous laisser la main">
        <Label>Mots-clés qui déclenchent le transfert (séparés par des virgules)</Label>
        <input
          value={cfg.transfer_keywords}
          onChange={(e) => set('transfer_keywords', e.target.value)}
          placeholder="réclamation, remboursement, urgent, responsable..."
          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
        />
        <Label className="mt-3">Message envoyé au client lors du transfert</Label>
        <input
          value={cfg.transfer_message}
          onChange={(e) => set('transfer_message', e.target.value)}
          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm"
        />
        <p className="text-gray-500 text-xs mt-2">
          💡 L'agent transfère aussi automatiquement quand il ne connaît pas la réponse — il n'invente jamais.
          Connectez Telegram (onglet Alertes) pour être prévenu instantanément.
        </p>
      </Section>

      {/* ── 6. CONSIGNES AVANCÉES (optionnel) ── */}
      <Section icon={Sparkles} title="Consignes spécifiques" subtitle="Optionnel — instructions particulières pour votre agent">
        <textarea
          value={cfg.extra_instructions}
          onChange={(e) => set('extra_instructions', e.target.value)}
          rows={3}
          placeholder="Ex : Toujours proposer la livraison gratuite à partir de 100 000 Ar. Ne jamais donner de remise supérieure à 10%."
          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500"
        />
      </Section>

      {/* ── SAVE ── */}
      <div className="sticky bottom-4">
        <button
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold shadow-2xl transition-all disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Enregistrer la configuration de l'agent
        </button>
      </div>
    </div>
  );
}

// ── Helpers ──
const updateProduct = (setCfg, index, key, value) =>
  setCfg(prev => ({
    ...prev,
    products: prev.products.map((p, i) => i === index ? { ...p, [key]: value } : p)
  }));

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-blue-600/30 flex items-center justify-center">
        <Icon className="w-4.5 h-4.5 text-blue-300" style={{ width: 18, height: 18 }} />
      </div>
      <div>
        <div className="text-white font-semibold text-sm">{title}</div>
        <div className="text-gray-500 text-xs">{subtitle}</div>
      </div>
    </div>
    {children}
  </div>
);

const Label = ({ children, className = '' }) => (
  <label className={`block text-gray-400 text-xs mb-1.5 ${className}`}>{children}</label>
);

const ChoiceCard = ({ active, onClick, label, desc }) => (
  <button
    onClick={onClick}
    className={`p-3 rounded-xl text-left transition-all border ${
      active
        ? 'bg-blue-600/30 border-blue-500/60 text-white'
        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
    }`}
  >
    <div className="text-sm font-medium">{label}</div>
    <div className="text-xs opacity-60 mt-0.5">{desc}</div>
  </button>
);
